import React, { useState } from 'react';
import { FiMail, FiPhone, FiPlus, FiTrash2, FiAlertTriangle, FiChevronDown, FiChevronUp, FiEdit3 } from 'react-icons/fi';
import type { AgentLicense, HREmployee, PtoPlan } from './hrData';
import type { SaveLicensePayload, UpdateHREmployeePayload } from '../../services/hrService';
import { getLicenseUrgency, daysUntil, usDate } from './licenseUtils';

interface Props {
  employees: HREmployee[];
  ptoPlans: PtoPlan[];
  busy: boolean;
  /** Resolves true once saved; false keeps the form open (the page shows the error). */
  onAllocatePto: (employeeId: number, hours: number) => Promise<boolean>;
  onUpdateEmployee: (employeeId: number, payload: UpdateHREmployeePayload) => Promise<boolean>;
  /** The per-employee license editor only renders when this is passed. */
  licenses?: {
    alertDays: number;
    /** Each resolves true once saved; false keeps the form filled in (the page shows the error). */
    onAdd: (employeeId: number, license: SaveLicensePayload) => Promise<boolean>;
    onUpdate: (licenseId: number, license: SaveLicensePayload) => Promise<boolean>;
    onRemove: (licenseId: number) => void;
    onCreateCommand: (employee: HREmployee, license: AgentLicense) => void;
  };
}

const URGENCY_LABEL: Record<string, string> = {
  expired: 'Expired',
  critical: 'Expires soon',
  warning: 'Renewal due',
  ok: '',
};

const initials = (name: string) => name.split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();

/** Admin-only — the employee roster with contact info, HR details, PTO balances, and per-state licenses. */
const HRDirectory: React.FC<Props> = ({ employees, ptoPlans, busy, onAllocatePto, onUpdateEmployee, licenses }) => {
  const [allocatingId, setAllocatingId] = useState<number | null>(null);
  const [hours, setHours] = useState('8');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editBirthday, setEditBirthday] = useState('');
  const [editPlanId, setEditPlanId] = useState('');
  const [managingId, setManagingId] = useState<number | null>(null);
  const [newState, setNewState] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [newLines, setNewLines] = useState('Property & Casualty');
  const [newExpiration, setNewExpiration] = useState('');
  /** The license being edited in the add form below the list, or null when adding. */
  const [editingLicenseId, setEditingLicenseId] = useState<number | null>(null);

  const today = new Date();

  const resetAddForm = () => {
    setNewState('');
    setNewNumber('');
    setNewLines('Property & Casualty');
    setNewExpiration('');
    setEditingLicenseId(null);
  };

  const startLicenseEdit = (lic: AgentLicense) => {
    setEditingLicenseId(lic.id);
    setNewState(lic.state);
    setNewNumber(lic.licenseNumber ?? '');
    setNewLines(lic.linesOfAuthority);
    setNewExpiration(lic.expirationDate);
  };

  const saveLicense = async (employeeId: number) => {
    if (!licenses || newState.trim().length !== 2 || !newExpiration) return;
    const payload: SaveLicensePayload = {
      state: newState.trim(),
      licenseNumber: newNumber.trim(),
      linesOfAuthority: newLines.trim() || 'Property & Casualty',
      expirationDate: newExpiration,
    };
    const saved = editingLicenseId !== null
      ? await licenses.onUpdate(editingLicenseId, payload)
      : await licenses.onAdd(employeeId, payload);
    if (saved) resetAddForm();
  };

  const startEdit = (e: HREmployee) => {
    setEditingId(e.id);
    setEditTitle(e.title ?? '');
    setEditStart(e.startDate ?? '');
    setEditBirthday(e.birthday ?? '');
    setEditPlanId(e.ptoPlanId ? String(e.ptoPlanId) : '');
  };

  const saveEdit = async (id: number) => {
    const saved = await onUpdateEmployee(id, {
      title: editTitle.trim() || null,
      startDate: editStart || null,
      birthday: editBirthday || null,
      ptoPlanId: editPlanId ? Number(editPlanId) : null,
    });
    if (saved) setEditingId(null);
  };

  const saveAllocation = async (id: number) => {
    const n = Number(hours);
    if (!n) { setAllocatingId(null); return; }
    if (await onAllocatePto(id, n)) setAllocatingId(null);
  };

  return (
    <div className="hr-dir">
      <div className="hr-dir__head">
        <h2 className="hr-dir__title">{employees.length} people</h2>
      </div>
      <ul className="hr-dir__list">
        {employees.map(e => (
          <li key={e.id} className="hr-dir__row">
            <span className="hr-dir__avatar">{initials(e.name)}</span>
            <span className="hr-dir__main">
              <span className="hr-dir__name">{e.name}</span>
              <span className="hr-dir__title-role">
                {[e.title, e.ptoPlanName].filter(Boolean).join(' · ') || 'No title yet'}
              </span>
            </span>
            <span className="hr-dir__contacts">
              {e.email && <a href={`mailto:${e.email}`} className="hr-dir__contact"><FiMail size={11} /> {e.email}</a>}
              {e.phone && <a href={`tel:${e.phone}`} className="hr-dir__contact"><FiPhone size={11} /> {e.phone}</a>}
            </span>
            <span className="hr-dir__pto">
              <span className="hr-dir__pto-label">PTO Balance</span>
              <span className={`hr-dir__pto-val${e.ptoBalanceHours < 0 ? ' hr-dir__pto-val--negative' : ''}`}>
                {e.ptoBalanceHours.toFixed(1)} hrs
              </span>
            </span>
            {allocatingId === e.id ? (
              <span className="hr-dir__allocate">
                <input
                  type="number"
                  step="0.5"
                  className="hr-dir__allocate-input"
                  value={hours}
                  onChange={ev => setHours(ev.target.value)}
                  aria-label="Hours to allocate (negative to remove)"
                  title="Use a negative number to remove hours"
                />
                <button type="button" className="hr-dir__allocate-save" disabled={busy} onClick={() => saveAllocation(e.id)}>
                  Save
                </button>
                <button type="button" className="hr-dir__allocate-cancel" onClick={() => setAllocatingId(null)}>Cancel</button>
              </span>
            ) : (
              <button type="button" className="hr-dir__allocate-btn" onClick={() => { setAllocatingId(e.id); setHours('8'); }}>
                <FiPlus size={12} aria-hidden="true" /> Allocate PTO
              </button>
            )}
            <button
              type="button"
              className="hr-dir__allocate-btn"
              onClick={() => (editingId === e.id ? setEditingId(null) : startEdit(e))}
              aria-expanded={editingId === e.id}
            >
              <FiEdit3 size={12} aria-hidden="true" /> Edit
            </button>

            {editingId === e.id && (
              <div className="hr-dir__edit">
                <label className="hr-to__field">
                  Title
                  <input value={editTitle} maxLength={150} onChange={ev => setEditTitle(ev.target.value)} placeholder="e.g. Producer" />
                </label>
                <label className="hr-to__field">
                  Start date
                  <input type="date" value={editStart} onChange={ev => setEditStart(ev.target.value)} />
                </label>
                <label className="hr-to__field">
                  Birthday
                  <input type="date" value={editBirthday} onChange={ev => setEditBirthday(ev.target.value)} />
                </label>
                <label className="hr-to__field">
                  PTO plan
                  <select value={editPlanId} onChange={ev => setEditPlanId(ev.target.value)}>
                    <option value="">No plan</option>
                    {ptoPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                <span className="hr-dir__edit-actions">
                  <button type="button" className="hr-dir__allocate-save" disabled={busy} onClick={() => saveEdit(e.id)}>Save</button>
                  <button type="button" className="hr-dir__allocate-cancel" onClick={() => setEditingId(null)}>Cancel</button>
                </span>
              </div>
            )}

            {licenses && (
              <span className="hr-dir__licenses">
                {e.licenses.length === 0 && managingId !== e.id && (
                  <span className="hr-lic-empty">No licenses on file</span>
                )}
                {e.licenses.map(lic => {
                  const days = daysUntil(lic.expirationDate, today);
                  const urgency = getLicenseUrgency(days, licenses.alertDays);
                  return (
                    <span key={lic.id} className={`hr-lic-chip${urgency !== 'ok' ? ` hr-lic-chip--${urgency}` : ''}`}>
                      {urgency !== 'ok' && <FiAlertTriangle size={10} aria-hidden="true" />}
                      {lic.state} · exp {usDate(lic.expirationDate)}
                    </span>
                  );
                })}
                <button
                  type="button"
                  className="hr-lic-manage-btn"
                  onClick={() => { setManagingId(managingId === e.id ? null : e.id); resetAddForm(); }}
                >
                  Manage Licenses {managingId === e.id ? <FiChevronUp size={11} /> : <FiChevronDown size={11} />}
                </button>
              </span>
            )}

            {licenses && managingId === e.id && (
              <div className="hr-lic-editor">
                {e.licenses.length > 0 && (
                  <ul className="hr-lic-editor__list">
                    {e.licenses.map(lic => {
                      const days = daysUntil(lic.expirationDate, today);
                      const urgency = getLicenseUrgency(days, licenses.alertDays);
                      return (
                        <li key={lic.id} className="hr-lic-editor__row">
                          <span className="hr-lic-editor__state">{lic.state}</span>
                          <span>{lic.linesOfAuthority}{lic.licenseNumber ? ` · #${lic.licenseNumber}` : ''}</span>
                          <span className="hr-lic-editor__exp">
                            {urgency === 'expired'
                              ? `Expired ${usDate(lic.expirationDate)}`
                              : `Expires ${usDate(lic.expirationDate)}${urgency !== 'ok' ? ` (${days}d)` : ''}`}
                          </span>
                          <button
                            type="button"
                            className="hr-lic-editor__edit"
                            onClick={() => startLicenseEdit(lic)}
                            aria-label={`Edit ${lic.state} license`}
                            title="Edit or renew"
                          >
                            <FiEdit3 size={12} />
                          </button>
                          {urgency !== 'ok' && (
                            lic.commandTaskId ? (
                              <span className="hr-lic-editor__cmd-done">Command created</span>
                            ) : (
                              <button
                                type="button"
                                className="hr-lic-editor__cmd-btn"
                                onClick={() => licenses.onCreateCommand(e, lic)}
                                title={URGENCY_LABEL[urgency]}
                              >
                                <FiAlertTriangle size={11} aria-hidden="true" /> Create Command
                              </button>
                            )
                          )}
                          <button
                            type="button"
                            className="hr-lic-editor__delete"
                            disabled={busy}
                            onClick={() => licenses.onRemove(lic.id)}
                            aria-label={`Remove ${lic.state} license`}
                          >
                            <FiTrash2 size={12} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="hr-lic-add">
                  <input
                    data-field="state"
                    placeholder="State"
                    maxLength={2}
                    value={newState}
                    onChange={ev => setNewState(ev.target.value.toUpperCase())}
                    aria-label="License state"
                  />
                  <input
                    data-field="licenseNumber"
                    placeholder="License #"
                    value={newNumber}
                    onChange={ev => setNewNumber(ev.target.value)}
                    aria-label="License number"
                  />
                  <input
                    data-field="linesOfAuthority"
                    placeholder="Lines of authority"
                    value={newLines}
                    onChange={ev => setNewLines(ev.target.value)}
                    aria-label="Lines of authority"
                  />
                  <input
                    type="date"
                    data-field="expirationDate"
                    value={newExpiration}
                    onChange={ev => setNewExpiration(ev.target.value)}
                    aria-label="Expiration date"
                  />
                  <button
                    type="button"
                    disabled={busy || newState.trim().length !== 2 || !newExpiration}
                    onClick={() => saveLicense(e.id)}
                  >
                    {editingLicenseId !== null ? 'Save License' : <><FiPlus size={12} aria-hidden="true" /> Add License</>}
                  </button>
                  {editingLicenseId !== null && (
                    <button type="button" className="hr-dir__allocate-cancel" onClick={resetAddForm}>Cancel</button>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default HRDirectory;
